/**
 * Platform Detection
 *
 * Utilities for detecting the current platform and adjusting behavior accordingly.
 */

// Platform type
let currentPlatform = 'web';

/**
 * Set the current platform
 * Call this at app initialization
 *
 * @param {'web' | 'ios' | 'android'} platform
 */
export function setPlatform(platform) {
  currentPlatform = platform;
}

/**
 * Get the current platform
 *
 * @returns {'web' | 'ios' | 'android'}
 */
export function getPlatform() {
  return currentPlatform;
}

/**
 * Check if running on web
 */
export function isWeb() {
  return currentPlatform === 'web';
}

/**
 * Check if running on mobile (iOS or Android)
 */
export function isMobile() {
  return currentPlatform === 'ios' || currentPlatform === 'android';
}

/**
 * Check if running on iOS
 */
export function isIOS() {
  return currentPlatform === 'ios';
}

/**
 * Check if running on Android
 */
export function isAndroid() {
  return currentPlatform === 'android';
}

/**
 * Execute platform-specific code
 *
 * @example
 * platformSelect({
 *   web: () => window.localStorage.getItem('key'),
 *   mobile: () => AsyncStorage.getItem('key'),
 * });
 */
export function platformSelect(options) {
  if (isWeb() && options.web) {
    return typeof options.web === 'function' ? options.web() : options.web;
  }
  if (isMobile() && options.mobile) {
    return typeof options.mobile === 'function' ? options.mobile() : options.mobile;
  }
  if (isIOS() && options.ios) {
    return typeof options.ios === 'function' ? options.ios() : options.ios;
  }
  if (isAndroid() && options.android) {
    return typeof options.android === 'function' ? options.android() : options.android;
  }
  if (options.default !== undefined) {
    return typeof options.default === 'function' ? options.default() : options.default;
  }
  return undefined;
}

export default {
  setPlatform,
  getPlatform,
  isWeb,
  isMobile,
  isIOS,
  isAndroid,
  platformSelect,
};
